/**
 * DrawingOverlay - Transparent drawing/annotation layer for video analysis
 *
 * Tools:
 * 1. Pen - freehand drawing for circling/marking
 * 2. Joint Angle - one-tap to measure angle from pose landmarks
 * 3. Two-Line Angle - draw two connected lines for manual angle measurement
 *
 * Renders via React Portal into the player's video container.
 * Z-index 15 (above video z-10, below controls z-20).
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Triangle, Slash, X, Undo2, Redo2, Trash2 } from 'lucide-react';

const JOINT_CHAINS = {
  25: [23, 25, 27],
  26: [24, 26, 28],
  27: [25, 27, 31],
  28: [26, 28, 32],
  23: [11, 23, 25],
  24: [12, 24, 26],
  13: [11, 13, 15],
  14: [12, 14, 16],
  11: [23, 11, 13],
  12: [24, 12, 14],
};

const PHASE_COLORS = {
  preparation: '#9C27B0',
  takeoff: '#F44336',
  air: '#2196F3',
  landing: '#4CAF50',
};

const DEFAULT_COLOR = '#FFFFFF';
const JOINT_PREVIEW_THRESHOLD = 0.07;
const POINT_SKIP_THRESHOLD = 0.002;
const LINE_WIDTH = 0.003;
const JOINT_LABELS = {
  11: 'L Shoulder',
  12: 'R Shoulder',
  13: 'L Elbow',
  14: 'R Elbow',
  23: 'L Hip',
  24: 'R Hip',
  25: 'L Knee',
  26: 'R Knee',
  27: 'L Ankle',
  28: 'R Ankle',
};
const GLOBAL_FRAME_KEY = '__global__';

function getLandmarkPoint(landmarks, idx) {
  const landmark = landmarks?.[idx];
  if (!landmark || typeof landmark.x !== 'number' || typeof landmark.y !== 'number') {
    return null;
  }
  return { x: landmark.x, y: landmark.y };
}

function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

function angleBetween(a, b, c) {
  const ba = { x: a.x - b.x, y: a.y - b.y };
  const bc = { x: c.x - b.x, y: c.y - b.y };
  const dot = ba.x * bc.x + ba.y * bc.y;
  const cross = ba.x * bc.y - ba.y * bc.x;
  return Math.abs(Math.atan2(cross, dot)) * (180 / Math.PI);
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function toNormalized(clientX, clientY, rect) {
  return {
    x: (clientX - rect.left) / rect.width,
    y: (clientY - rect.top) / rect.height,
  };
}

function drawPenStroke(ctx, points, color, width, w, h, opacity) {
  if (points.length < 2) return;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = color;
  ctx.lineWidth = width * w;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(points[0].x * w, points[0].y * h);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x * w, points[i].y * h);
  }
  ctx.stroke();
  ctx.restore();
}

function drawAngleAnnotation(ctx, points, color, w, h, opacity, angle) {
  if (points.length < 3) return;
  const [a, b, c] = points;
  const px = p => p.x * w;
  const py = p => p.y * h;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';

  ctx.beginPath();
  ctx.moveTo(px(a), py(a));
  ctx.lineTo(px(b), py(b));
  ctx.lineTo(px(c), py(c));
  ctx.stroke();

  const radius = Math.min(30, dist(a, b) * w * 0.3, dist(c, b) * w * 0.3);
  const angleStart = Math.atan2(py(a) - py(b), px(a) - px(b));
  const angleEnd = Math.atan2(py(c) - py(b), px(c) - px(b));

  let sweep = angleEnd - angleStart;
  if (sweep > Math.PI) sweep -= 2 * Math.PI;
  if (sweep < -Math.PI) sweep += 2 * Math.PI;

  ctx.beginPath();
  ctx.arc(px(b), py(b), radius, angleStart, angleStart + sweep, sweep < 0);
  ctx.stroke();

  const midAngle = angleStart + sweep / 2;
  const labelDist = radius + 14;
  const lx = px(b) + Math.cos(midAngle) * labelDist;
  const ly = py(b) + Math.sin(midAngle) * labelDist;

  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const text = `${Math.round(angle)}°`;
  const metrics = ctx.measureText(text);
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(lx - metrics.width / 2 - 3, ly - 9, metrics.width + 6, 18);
  ctx.fillStyle = color;
  ctx.fillText(text, lx, ly);

  ctx.fillStyle = color;
  for (const p of [a, b, c]) {
    ctx.beginPath();
    ctx.arc(px(p), py(p), 4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawAnnotation(ctx, ann, w, h, opacity) {
  if (ann.tool === 'pen') {
    drawPenStroke(ctx, ann.points, ann.color, ann.lineWidth, w, h, opacity);
  } else if (ann.tool === 'jointAngle' || ann.tool === 'twoLineAngle') {
    drawAngleAnnotation(ctx, ann.points, ann.color, w, h, opacity, ann.angle);
  }
}

function drawJointMarker(ctx, point, w, h, isHighlighted, label) {
  const x = point.x * w;
  const y = point.y * h;

  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, isHighlighted ? 10 : 7, 0, Math.PI * 2);
  ctx.fillStyle = isHighlighted ? 'rgba(96,165,250,0.28)' : 'rgba(255,255,255,0.18)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y, isHighlighted ? 5 : 4, 0, Math.PI * 2);
  ctx.fillStyle = isHighlighted ? '#60a5fa' : 'rgba(255,255,255,0.92)';
  ctx.fill();

  ctx.lineWidth = isHighlighted ? 2 : 1.5;
  ctx.strokeStyle = isHighlighted ? 'rgba(96,165,250,0.95)' : 'rgba(255,255,255,0.75)';
  ctx.stroke();

  if (isHighlighted && label) {
    ctx.font = '600 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const textWidth = ctx.measureText(label).width;
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(x - textWidth / 2 - 6, y - 28, textWidth + 12, 18);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, x, y - 14);
  }

  ctx.restore();
}

function getLandmarkConfidence(landmark) {
  if (!landmark) return 0;
  return landmark.confidence ?? landmark.visibility ?? 0;
}

const styles = {
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 15,
    cursor: 'crosshair',
  },
  canvasInactive: {
    pointerEvents: 'none',
    cursor: 'default',
  },
  toolbar: isCompact => ({
    position: 'absolute',
    top: '50%',
    right: '5%',
    transform: 'translateY(-50%)',
    zIndex: 25,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: isCompact ? 6 : 8,
    width: isCompact ? 44 : 48,
    maxHeight: 'calc(100% - 16px)',
    padding: isCompact ? '6px 5px' : '8px 6px',
    borderRadius: isCompact ? 14 : 16,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.15)',
    userSelect: 'none',
    pointerEvents: 'auto',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  }),
  toolGroup: isCompact => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: isCompact ? 4 : 6,
    width: '100%',
  }),
  colorGroup: isCompact => ({
    display: 'grid',
    gridTemplateColumns: isCompact ? 'repeat(2, minmax(0, 1fr))' : '1fr',
    gap: isCompact ? 4 : 8,
    width: '100%',
    padding: isCompact ? '0 2px' : 0,
    justifyItems: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
  }),
  toolBtn: (active, isCompact) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isCompact ? 32 : 36,
    height: isCompact ? 32 : 36,
    borderRadius: 8,
    border: 'none',
    background: active ? 'rgba(96,165,250,0.4)' : 'transparent',
    color: active ? '#60a5fa' : 'rgba(255,255,255,0.8)',
    cursor: 'pointer',
    padding: 0,
    transition: 'background 0.15s',
  }),
  divider: {
    width: '100%',
    height: 1,
    background: 'rgba(255,255,255,0.2)',
    margin: '2px 0',
  },
  colorSwatch: (color, isSelected, isCompact) => ({
    width: isCompact ? 16 : 20,
    height: isCompact ? 16 : 20,
    borderRadius: '50%',
    border: isSelected ? '2px solid #fff' : '2px solid transparent',
    background: color,
    cursor: 'pointer',
    padding: 0,
    outline: 'none',
    boxSizing: 'border-box',
    display: 'block',
    flexShrink: 0,
  }),
  twoLineHint: {
    position: 'absolute',
    top: 12,
    right: 64,
    zIndex: 25,
    padding: '4px 12px',
    borderRadius: 12,
    background: 'rgba(0,0,0,0.5)',
    color: '#60a5fa',
    fontSize: 12,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.15)',
  },
};

const SWATCH_COLORS = ['#FFFFFF', '#F44336', '#2196F3', '#4CAF50', '#9C27B0', '#FFC107'];
const COMPACT_SWATCH_COLORS = ['#FFFFFF', '#F44336'];

const DrawingOverlay = ({
  isActive,
  currentFrame,
  videoId,
  containerEl,
  fallbackContainerEl,
  poseData,
  phaseColor,
  onExit,
}) => {
  const canvasRef = useRef(null);
  const [annotations, setAnnotations] = useState({});
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [activeTool, setActiveTool] = useState('pen');
  const [strokeColor, setStrokeColor] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [twoLineState, setTwoLineState] = useState('idle');
  const [firstLine, setFirstLine] = useState(null);
  const [liveAngle, setLiveAngle] = useState(null);
  const [hoverJoint, setHoverJoint] = useState(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const activeColor = useMemo(() => {
    if (strokeColor) return strokeColor;
    return (phaseColor && PHASE_COLORS[phaseColor]) || DEFAULT_COLOR;
  }, [strokeColor, phaseColor]);

  const availableJoints = useMemo(() => {
    if (!poseData?.pose_landmarks) return [];
    const landmarks = poseData.pose_landmarks;

    return Object.keys(JOINT_CHAINS)
      .map(jointIdx => parseInt(jointIdx, 10))
      .map(idx => {
        const point = getLandmarkPoint(landmarks, idx);
        const points = JOINT_CHAINS[idx].map(chainIdx => getLandmarkPoint(landmarks, chainIdx));

        return {
          idx,
          point,
          points,
          label: JOINT_LABELS[idx] || 'Joint',
          isVisible: !!point && getLandmarkConfidence(landmarks[idx]) > 0.5,
          isMeasurable: points.every(Boolean) && JOINT_CHAINS[idx].every(chainIdx => getLandmarkConfidence(landmarks[chainIdx]) > 0.5),
        };
      })
      .filter(joint => joint.isVisible);
  }, [poseData]);

  useEffect(() => {
    const observedEl = containerEl || fallbackContainerEl;
    if (!observedEl) return;

    const syncSize = () => {
      const rect = observedEl.getBoundingClientRect();
      setContainerSize({
        width: rect.width,
        height: rect.height,
      });
    };

    const resizeObserver = new ResizeObserver(syncSize);
    resizeObserver.observe(observedEl);
    syncSize();

    return () => resizeObserver.disconnect();
  }, [containerEl, fallbackContainerEl]);

  const isCompactToolbar = useMemo(() => {
    const { width, height } = containerSize;
    return width > 0 && height > 0 && (width <= 768 || height <= 430);
  }, [containerSize]);

  const paletteColors = isCompactToolbar ? COMPACT_SWATCH_COLORS : SWATCH_COLORS;
  const saveTimerRef = useRef(null);

  useEffect(() => {
    const key = `lumi-draw-${videoId}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') setAnnotations(parsed);
      }
    } catch {}
  }, [videoId]);

  const persistAnnotations = useCallback(anns => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(`lumi-draw-${videoId}`, JSON.stringify(anns));
      } catch {}
    }, 500);
  }, [videoId]);

  const pushUndo = useCallback(action => {
    setUndoStack(prev => [...prev.slice(-49), action]);
    setRedoStack([]);
  }, []);

  const addAnnotation = useCallback(ann => {
    const frameKey = ann.tool === 'pen' ? GLOBAL_FRAME_KEY : ann.frameNumber;
    const storedAnnotation = { ...ann, storageFrameKey: frameKey };
    setAnnotations(prev => {
      const next = { ...prev, [frameKey]: [...(prev[frameKey] || []), storedAnnotation] };
      persistAnnotations(next);
      return next;
    });
    pushUndo({ type: 'add', annotation: storedAnnotation });
  }, [persistAnnotations, pushUndo]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const action = prev[prev.length - 1];
      const rest = prev.slice(0, -1);

      if (action.type === 'add') {
        const { annotation: ann } = action;
        setAnnotations(a => {
          const frameKey = ann.storageFrameKey ?? ann.frameNumber;
          const next = { ...a, [frameKey]: (a[frameKey] || []).filter(x => x.id !== ann.id) };
          persistAnnotations(next);
          return next;
        });
        setRedoStack(r => [...r, action]);
      } else if (action.type === 'clear') {
        setAnnotations(a => {
          const next = { ...a };
          Object.entries(action.removedByFrame || {}).forEach(([frameKey, removed]) => {
            next[frameKey] = removed;
          });
          persistAnnotations(next);
          return next;
        });
        setRedoStack(r => [...r, action]);
      }
      return rest;
    });
  }, [persistAnnotations]);

  const redo = useCallback(() => {
    setRedoStack(prev => {
      if (prev.length === 0) return prev;
      const action = prev[prev.length - 1];
      const rest = prev.slice(0, -1);

      if (action.type === 'add') {
        const { annotation: ann } = action;
        setAnnotations(a => {
          const frameKey = ann.storageFrameKey ?? ann.frameNumber;
          const next = { ...a, [frameKey]: [...(a[frameKey] || []), ann] };
          persistAnnotations(next);
          return next;
        });
        setUndoStack(u => [...u, action]);
      } else if (action.type === 'clear') {
        setAnnotations(a => {
          const next = { ...a };
          Object.keys(action.removedByFrame || {}).forEach(frameKey => {
            next[frameKey] = [];
          });
          persistAnnotations(next);
          return next;
        });
        setUndoStack(u => [...u, action]);
      }
      return rest;
    });
  }, [persistAnnotations]);

  const clearFrame = useCallback(() => {
    const frameAnns = annotations[currentFrame] || [];
    const globalAnns = annotations[GLOBAL_FRAME_KEY] || [];
    if (frameAnns.length === 0 && globalAnns.length === 0) return;

    const removedByFrame = {};
    if (frameAnns.length > 0) removedByFrame[currentFrame] = frameAnns;
    if (globalAnns.length > 0) removedByFrame[GLOBAL_FRAME_KEY] = globalAnns;

    pushUndo({ type: 'clear', removedByFrame });
    setAnnotations(prev => {
      const next = { ...prev };
      if (frameAnns.length > 0) next[currentFrame] = [];
      if (globalAnns.length > 0) next[GLOBAL_FRAME_KEY] = [];
      persistAnnotations(next);
      return next;
    });
    setTwoLineState('idle');
    setFirstLine(null);
    setLiveAngle(null);
  }, [annotations, currentFrame, pushUndo, persistAnnotations]);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    ctx.clearRect(0, 0, w, h);

    for (let offset = -2; offset <= 2; offset++) {
      const frame = currentFrame + offset;
      const frameAnns = annotations[frame];
      if (!frameAnns) continue;
      const opacity = offset === 0 ? 1.0 : Math.abs(offset) === 1 ? 0.5 : 0.2;
      const useDash = offset !== 0;

      for (const ann of frameAnns) {
        if (ann.tool === 'pen' && offset !== 0) continue;

        if (useDash && ann.tool !== 'pen') {
          ctx.save();
          ctx.setLineDash([6, 4]);
          drawAnnotation(ctx, ann, w, h, opacity);
          ctx.setLineDash([]);
          ctx.restore();
        } else {
          drawAnnotation(ctx, ann, w, h, opacity);
        }
      }
    }

    const globalPenAnnotations = annotations[GLOBAL_FRAME_KEY] || [];
    for (const ann of globalPenAnnotations) {
      drawAnnotation(ctx, ann, w, h, 1.0);
    }

    if (isDrawing && currentStroke.length > 1 && activeTool === 'pen') {
      drawPenStroke(ctx, currentStroke, activeColor, LINE_WIDTH, w, h, 1.0);
    }

    if (activeTool === 'twoLineAngle') {
      if (!firstLine && isDrawing && currentStroke.length >= 2) {
        const a = currentStroke[0];
        const b = currentStroke[currentStroke.length - 1];
        ctx.save();
        ctx.strokeStyle = activeColor;
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(a.x * w, a.y * h);
        ctx.lineTo(b.x * w, b.y * h);
        ctx.stroke();
        ctx.fillStyle = activeColor;
        ctx.beginPath();
        ctx.arc(a.x * w, a.y * h, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (firstLine) {
        const { a, b } = firstLine;
        ctx.save();
        ctx.strokeStyle = activeColor;
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(a.x * w, a.y * h);
        ctx.lineTo(b.x * w, b.y * h);
        ctx.stroke();
        ctx.fillStyle = activeColor;
        ctx.beginPath();
        ctx.arc(b.x * w, b.y * h, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (isDrawing && currentStroke.length > 0) {
          const c = currentStroke[currentStroke.length - 1];
          const angle = angleBetween(a, b, c);
          drawAngleAnnotation(ctx, [a, b, c], activeColor, w, h, 0.8, angle);
        }
      }
    }

    if (isActive && activeTool === 'jointAngle') {
      for (const joint of availableJoints) {
        drawJointMarker(
          ctx,
          joint.point,
          w,
          h,
          hoverJoint?.idx === joint.idx,
          joint.isMeasurable ? joint.label : `${joint.label} (preview only)`
        );
      }

      if (hoverJoint?.isMeasurable) {
        const previewAngle = angleBetween(hoverJoint.points[0], hoverJoint.points[1], hoverJoint.points[2]);
        drawAngleAnnotation(ctx, hoverJoint.points, activeColor, w, h, 0.9, previewAngle);
      }
    }
  }, [annotations, currentFrame, isDrawing, currentStroke, activeTool, firstLine, activeColor, isActive, availableJoints, hoverJoint]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !containerEl) return;

    const syncSize = () => {
      const rect = containerEl.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    const resizeObserver = new ResizeObserver(syncSize);
    resizeObserver.observe(containerEl);
    syncSize();

    return () => resizeObserver.disconnect();
  }, [containerEl]);

  const findNearestJoint = useCallback(normPoint => {
    if (availableJoints.length === 0) return null;
    let closest = null;
    let minDist = JOINT_PREVIEW_THRESHOLD;

    for (const joint of availableJoints) {
      const d = dist(normPoint, joint.point);
      if (d < minDist) {
        minDist = d;
        closest = joint;
      }
    }
    return closest;
  }, [availableJoints]);

  const handlePointerDown = useCallback(e => {
    if (!isActive) return;
    if (e.pointerType === 'touch' && e.isPrimary === false) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);

    const rect = canvas.getBoundingClientRect();
    const norm = toNormalized(e.clientX, e.clientY, rect);

    if (activeTool === 'jointAngle') {
      const joint = findNearestJoint(norm) || hoverJoint;
      if (!joint) return;
      if (!joint.isMeasurable) {
        setHoverJoint(joint);
        return;
      }

      const angle = angleBetween(joint.points[0], joint.points[1], joint.points[2]);
      addAnnotation({
        id: generateUUID(),
        frameNumber: currentFrame,
        tool: 'jointAngle',
        points: joint.points,
        color: activeColor,
        lineWidth: LINE_WIDTH,
        angle,
      });
      return;
    }

    if (activeTool === 'twoLineAngle') {
      if (twoLineState === 'idle') {
        setIsDrawing(true);
        setCurrentStroke([norm]);
      } else if (twoLineState === 'line1Done' && firstLine) {
        setIsDrawing(true);
        setCurrentStroke([firstLine.b, norm]);
      }
      return;
    }

    setIsDrawing(true);
    setCurrentStroke([norm]);
  }, [isActive, activeTool, findNearestJoint, hoverJoint, activeColor, currentFrame, addAnnotation, twoLineState, firstLine]);

  const handlePointerMove = useCallback(e => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const norm = toNormalized(e.clientX, e.clientY, rect);

    if (activeTool === 'jointAngle') {
      setHoverJoint(findNearestJoint(norm));
      return;
    }

    if (!isDrawing) return;

    if (activeTool === 'pen') {
      if (currentStroke.length > 0) {
        const last = currentStroke[currentStroke.length - 1];
        if (dist(norm, last) < POINT_SKIP_THRESHOLD) return;
      }
      setCurrentStroke(prev => [...prev, norm]);
    } else if (activeTool === 'twoLineAngle') {
      if (twoLineState === 'idle') {
        setCurrentStroke(prev => [prev[0], norm]);
      } else if (twoLineState === 'line1Done' && firstLine) {
        setCurrentStroke([firstLine.b, norm]);
        const angle = angleBetween(firstLine.a, firstLine.b, norm);
        setLiveAngle(angle);
      }
    }
  }, [isDrawing, activeTool, currentStroke, twoLineState, firstLine, findNearestJoint]);

  const handlePointerUp = useCallback(e => {
    if (!isDrawing) return;
    setIsDrawing(false);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const norm = toNormalized(e.clientX, e.clientY, rect);

    if (activeTool === 'pen') {
      const points = [...currentStroke, norm];
      if (points.length >= 2) {
        addAnnotation({
          id: generateUUID(),
          frameNumber: currentFrame,
          tool: 'pen',
          points,
          color: activeColor,
          lineWidth: LINE_WIDTH,
          angle: null,
        });
      }
    } else if (activeTool === 'twoLineAngle') {
      if (twoLineState === 'idle') {
        const a = currentStroke[0];
        const b = norm;
        if (dist(a, b) > 0.01) {
          setFirstLine({ a, b });
          setTwoLineState('line1Done');
        }
      } else if (twoLineState === 'line1Done' && firstLine) {
        const c = norm;
        if (dist(firstLine.b, c) > 0.01) {
          const angle = angleBetween(firstLine.a, firstLine.b, c);
          addAnnotation({
            id: generateUUID(),
            frameNumber: currentFrame,
            tool: 'twoLineAngle',
            points: [firstLine.a, firstLine.b, c],
            color: activeColor,
            lineWidth: LINE_WIDTH,
            angle,
          });
        }
        setTwoLineState('idle');
        setFirstLine(null);
        setLiveAngle(null);
      }
    }

    setCurrentStroke([]);
  }, [isDrawing, activeTool, currentStroke, currentFrame, activeColor, addAnnotation, twoLineState, firstLine]);

  useEffect(() => {
    setTwoLineState('idle');
    setFirstLine(null);
    setLiveAngle(null);
    setCurrentStroke([]);
    setIsDrawing(false);
    setHoverJoint(null);
  }, [activeTool]);

  useEffect(() => {
    if (!isActive) return;

    const handleKey = e => {
      if (e.key === 'Escape') {
        onExit();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        e.stopPropagation();
        undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault();
        e.stopPropagation();
        redo();
        return;
      }
      if (e.key === '1') setActiveTool('pen');
      if (e.key === '2') setActiveTool('jointAngle');
      if (e.key === '3') setActiveTool('twoLineAngle');
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isActive, onExit, undo, redo]);

  const renderInContainer = useCallback((element, target, key) => {
    if (!element) return null;
    if (!target) return React.cloneElement(element, { key });
    return ReactDOM.createPortal(element, target, key);
  }, []);

  const canvasStyle = {
    ...styles.canvas,
    ...(isActive ? {} : styles.canvasInactive),
    touchAction: isActive ? 'none' : 'auto',
  };

  const hasJointAngle = !!poseData?.pose_landmarks;

  const canvasElement = containerEl ? (
    <canvas
      ref={canvasRef}
      style={canvasStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={() => setHoverJoint(null)}
      onPointerCancel={() => {
        setIsDrawing(false);
        setCurrentStroke([]);
        setHoverJoint(null);
      }}
    />
  ) : null;

  const toolbarElement = isActive ? (
    <div style={styles.toolbar(isCompactToolbar)}>
      <div style={styles.toolGroup(isCompactToolbar)}>
        <button
          style={styles.toolBtn(activeTool === 'pen', isCompactToolbar)}
          onClick={() => setActiveTool('pen')}
          title="Pen (1)"
        >
          <Pencil size={isCompactToolbar ? 15 : 16} />
        </button>
        <button
          style={{
            ...styles.toolBtn(activeTool === 'jointAngle', isCompactToolbar),
            opacity: hasJointAngle ? 1 : 0.3,
          }}
          onClick={() => hasJointAngle && setActiveTool('jointAngle')}
          title={hasJointAngle ? 'Joint Angle (2)' : 'Joint Angle (no pose data)'}
          disabled={!hasJointAngle}
        >
          <Triangle size={isCompactToolbar ? 15 : 16} />
        </button>
        <button
          style={styles.toolBtn(activeTool === 'twoLineAngle', isCompactToolbar)}
          onClick={() => setActiveTool('twoLineAngle')}
          title="Two-Line Angle (3)"
        >
          <Slash size={isCompactToolbar ? 15 : 16} />
        </button>
      </div>

      <div style={styles.divider} />

      <div style={styles.colorGroup(isCompactToolbar)}>
        {paletteColors.map(c => (
          <button
            key={c}
            style={styles.colorSwatch(c, strokeColor === c, isCompactToolbar)}
            onClick={() => setStrokeColor(strokeColor === c ? null : c)}
            title={strokeColor === c ? 'Auto color' : c}
          />
        ))}
      </div>

      <div style={styles.divider} />

      <div style={styles.toolGroup(isCompactToolbar)}>
        <button
          style={{ ...styles.toolBtn(false, isCompactToolbar), opacity: undoStack.length > 0 ? 1 : 0.3 }}
          onClick={undo}
          disabled={undoStack.length === 0}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 size={isCompactToolbar ? 15 : 16} />
        </button>
        <button
          style={{ ...styles.toolBtn(false, isCompactToolbar), opacity: redoStack.length > 0 ? 1 : 0.3 }}
          onClick={redo}
          disabled={redoStack.length === 0}
          title="Redo (Ctrl+Y)"
        >
          <Redo2 size={isCompactToolbar ? 15 : 16} />
        </button>
        <button
          style={styles.toolBtn(false, isCompactToolbar)}
          onClick={clearFrame}
          title="Clear frame"
        >
          <Trash2 size={isCompactToolbar ? 15 : 16} />
        </button>
      </div>

      <div style={styles.divider} />

      <div style={styles.toolGroup(isCompactToolbar)}>
        <button
          style={styles.toolBtn(false, isCompactToolbar)}
          onClick={onExit}
          title="Exit draw mode (Esc)"
        >
          <X size={isCompactToolbar ? 15 : 16} />
        </button>
      </div>
    </div>
  ) : null;

  const hintElement = isActive && activeTool === 'twoLineAngle' ? (
    <div style={styles.twoLineHint}>
      {twoLineState === 'idle'
        ? 'Drag first line along the limb'
        : `${liveAngle !== null ? `${Math.round(liveAngle)}° - ` : ''}Drag second line from endpoint`}
    </div>
  ) : null;

  const jointHintElement = isActive && activeTool === 'jointAngle' && availableJoints.length > 0 ? (
    <div style={styles.twoLineHint}>
      {hoverJoint
        ? hoverJoint.isMeasurable
          ? `${hoverJoint.label} • tap to measure`
          : `${hoverJoint.label} • move to a clearer frame`
        : `Tap one of ${availableJoints.length} highlighted joints`}
    </div>
  ) : null;

  const uiTarget = containerEl || fallbackContainerEl;

  return (
    <>
      {renderInContainer(canvasElement, containerEl, 'drawing-overlay-canvas')}
      {renderInContainer(toolbarElement, uiTarget, 'drawing-overlay-toolbar')}
      {renderInContainer(hintElement, uiTarget, 'drawing-overlay-hint')}
      {renderInContainer(jointHintElement, uiTarget, 'drawing-overlay-joint-hint')}
    </>
  );
};

export default DrawingOverlay;
